import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { UserPlus, Trash2, Copy, ShieldCheck, X, Check, Mail } from "lucide-react";

const emptyInvite = { name: "", email: "", capabilities: [] };

/**
 * Admin-only page to manage HBT internal team.
 *  - Lists admins + staff (active and pending)
 *  - Invite a new staff member with a capability picker
 *  - Toggle capabilities on active staff in-place
 *  - Remove staff (admins protected)
 */
export default function Staff() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [caps, setCaps] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState(emptyInvite);
  const [busy, setBusy] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    const [staff, catalog] = await Promise.all([
      api.get("/auth/staff"),
      api.get("/auth/capabilities"),
    ]);
    setRows(staff.data);
    setCaps(catalog.data);
  };

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user]);

  if (user && user.role !== "admin") {
    return <Navigate to="/app" replace />;
  }

  const toggleFormCap = (key) => {
    setForm((f) => ({
      ...f,
      capabilities: f.capabilities.includes(key)
        ? f.capabilities.filter((c) => c !== key)
        : [...f.capabilities, key],
    }));
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.capabilities.length) {
      toast.error("Pick at least one capability so this teammate can actually do something.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/invite", {
        email: form.email,
        name: form.name,
        role: "staff",
        capabilities: form.capabilities,
      });
      setInviteResult(data);
      toast.success("Invite sent");
      setForm(emptyInvite);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleRowCap = async (row, key) => {
    if (row.role !== "staff") return;
    const next = row.capabilities.includes(key)
      ? row.capabilities.filter((c) => c !== key)
      : [...row.capabilities, key];
    setSavingId(row.user_id);
    try {
      await api.patch(`/auth/staff/${row.user_id}/capabilities`, { capabilities: next });
      setRows((prev) => prev.map((r) => r.user_id === row.user_id ? { ...r, capabilities: next } : r));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSavingId(null);
    }
  };

  const removeStaff = async (row) => {
    if (!window.confirm(`Remove ${row.name || row.email}?`)) return;
    try {
      await api.delete(`/auth/staff/${row.user_id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  const resendInvite = async (row) => {
    try {
      const { data } = await api.post(`/auth/resend-invite/${row.user_id}`);
      toast.success("Invite re-sent");
      setInviteResult({ ...data, name: row.name });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    }
  };

  const admins = rows.filter((r) => r.role === "admin");
  const staffActive = rows.filter((r) => r.role === "staff" && r.active);
  const staffPending = rows.filter((r) => r.role === "staff" && !r.active);

  return (
    <div>
      <PageHeader
        eyebrow="Team"
        title="Staff"
        subtitle="Manage your HBT team and pick what each teammate can access."
        actions={
          <button
            onClick={() => { setShowInvite(true); setInviteResult(null); setForm(emptyInvite); }}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="invite-staff-btn"
          >
            <UserPlus className="w-4 h-4" /> Invite staff
          </button>
        }
      />
      <div className="px-8 lg:px-12 py-8 space-y-8">

        {/* Admins */}
        <section data-testid="admins-section">
          <div className="hbt-label mb-3">Administrators</div>
          <div className="hbt-card overflow-hidden">
            <table className="hbt-table w-full">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Last login</th><th></th></tr>
              </thead>
              <tbody>
                {admins.map((a) => (
                  <tr key={a.user_id} data-testid={`admin-row-${a.user_id}`}>
                    <td>{a.name} {a.is_self && <span className="text-xs text-neutral-500 ml-1">(you)</span>}</td>
                    <td className="text-neutral-600">{a.email}</td>
                    <td className="text-neutral-500 text-xs">{a.last_login_at ? new Date(a.last_login_at).toLocaleString() : "—"}</td>
                    <td className="text-right">
                      <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded-full" style={{ background: "rgba(26,54,45,0.08)", color: "var(--forest)" }}>Full access</span>
                    </td>
                  </tr>
                ))}
                {admins.length === 0 && (
                  <tr><td colSpan={4} className="text-center text-neutral-500 py-6">No administrators yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Active staff */}
        <section data-testid="staff-active-section">
          <div className="hbt-label mb-3">Staff · {staffActive.length}</div>
          {staffActive.length === 0 ? (
            <div className="hbt-card p-8 text-center text-sm text-neutral-500">
              No staff yet. Invite your first teammate above.
            </div>
          ) : (
            <div className="space-y-3">
              {staffActive.map((s) => (
                <div key={s.user_id} className="hbt-card p-5" data-testid={`staff-row-${s.user_id}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <div className="font-semibold">{s.name}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">{s.email} · last seen {s.last_login_at ? new Date(s.last_login_at).toLocaleDateString() : "—"}</div>
                    </div>
                    <button
                      onClick={() => removeStaff(s)}
                      className="btn-outline text-xs inline-flex items-center gap-1.5"
                      style={{ color: "var(--terracotta,#C85A3C)" }}
                      data-testid={`remove-staff-${s.user_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  </div>
                  <div className="hbt-label text-[10px] mb-2">Capabilities {savingId === s.user_id && <span className="text-neutral-400 ml-1">saving…</span>}</div>
                  <div className="flex flex-wrap gap-2">
                    {caps.map((c) => {
                      const active = s.capabilities.includes(c.key);
                      return (
                        <button
                          key={c.key}
                          type="button"
                          onClick={() => toggleRowCap(s, c.key)}
                          className="text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition-colors"
                          style={{
                            borderColor: active ? "var(--forest)" : "rgba(10,10,10,0.15)",
                            background: active ? "var(--forest)" : "transparent",
                            color: active ? "#F6F1E7" : "var(--forest)",
                          }}
                          data-testid={`cap-${s.user_id}-${c.key}`}
                        >
                          {active ? <Check className="w-3 h-3" /> : <span className="w-3" />} {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Pending invites */}
        {staffPending.length > 0 && (
          <section data-testid="staff-pending-section">
            <div className="hbt-label mb-3">Pending invites · {staffPending.length}</div>
            <div className="hbt-card overflow-hidden">
              <table className="hbt-table w-full">
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Capabilities</th><th>Status</th><th></th></tr>
                </thead>
                <tbody>
                  {staffPending.map((p) => (
                    <tr key={p.user_id} data-testid={`pending-row-${p.user_id}`}>
                      <td>{p.name}</td>
                      <td className="text-neutral-600">{p.email}</td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {(p.capabilities || []).map((k) => {
                            const label = caps.find((c) => c.key === k)?.label || k;
                            return <span key={k} className="inline-block text-[10px] px-2 py-1 rounded-full" style={{ background: "rgba(26,54,45,0.06)", color: "var(--forest)" }}>{label}</span>;
                          })}
                        </div>
                      </td>
                      <td>
                        <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded-full" style={{
                          background: p.status === "expired" ? "rgba(200,90,60,0.12)" : "rgba(26,54,45,0.08)",
                          color: p.status === "expired" ? "var(--terracotta,#C85A3C)" : "var(--forest)",
                        }}>{p.status}</span>
                      </td>
                      <td className="text-right">
                        <div className="inline-flex gap-1.5">
                          <button onClick={() => resendInvite(p)} className="btn-outline text-xs inline-flex items-center gap-1" data-testid={`resend-${p.user_id}`}>
                            <Mail className="w-3.5 h-3.5" /> Resend
                          </button>
                          <button onClick={() => removeStaff(p)} className="btn-outline text-xs inline-flex items-center gap-1" style={{ color: "var(--terracotta,#C85A3C)" }} data-testid={`cancel-invite-${p.user_id}`}>
                            <X className="w-3.5 h-3.5" /> Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>

      {/* Invite modal */}
      {showInvite && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && setShowInvite(false)}>
          <div className="hbt-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="invite-modal">
            {inviteResult ? (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-5 h-5" style={{ color: "var(--forest)" }} />
                  <div className="font-display text-xl font-semibold">Invite sent</div>
                </div>
                <p className="text-sm text-neutral-600 mb-4">We&apos;ve emailed a link to set their password. Share this URL over WhatsApp/SMS if the email doesn&apos;t land:</p>
                <div className="hbt-input font-mono text-xs break-all bg-neutral-50" data-testid="invite-url-display">{inviteResult.invite_url}</div>
                <div className="flex justify-end gap-2 mt-5">
                  <button onClick={() => { navigator.clipboard.writeText(inviteResult.invite_url); toast.success("Copied"); }} className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="copy-invite-url">
                    <Copy className="w-3.5 h-3.5" /> Copy URL
                  </button>
                  <button onClick={() => { setShowInvite(false); setInviteResult(null); }} className="btn-primary" data-testid="close-invite-modal">Done</button>
                </div>
              </div>
            ) : (
              <form onSubmit={sendInvite}>
                <div className="font-display text-xl font-semibold mb-1">Invite a teammate</div>
                <p className="text-xs text-neutral-500 mb-5">They&apos;ll get an email to pick their password. Tick only what they need.</p>
                <div className="space-y-3">
                  <div>
                    <label className="hbt-label block mb-2">Full name</label>
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="hbt-input" data-testid="invite-name" />
                  </div>
                  <div>
                    <label className="hbt-label block mb-2">Work email</label>
                    <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="hbt-input" data-testid="invite-email" />
                  </div>
                  <div>
                    <label className="hbt-label block mb-2">Capabilities</label>
                    <div className="flex flex-wrap gap-2">
                      {caps.map((c) => {
                        const active = form.capabilities.includes(c.key);
                        return (
                          <button
                            key={c.key}
                            type="button"
                            onClick={() => toggleFormCap(c.key)}
                            className="text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5"
                            style={{
                              borderColor: active ? "var(--forest)" : "rgba(10,10,10,0.15)",
                              background: active ? "var(--forest)" : "transparent",
                              color: active ? "#F6F1E7" : "var(--forest)",
                            }}
                            data-testid={`invite-cap-${c.key}`}
                          >
                            {active ? <Check className="w-3 h-3" /> : <span className="w-3" />} {c.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <button type="button" onClick={() => setShowInvite(false)} className="btn-outline" disabled={busy}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={busy} data-testid="send-invite-btn">
                    {busy ? "Sending…" : "Send invite"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
